import React, { useMemo, useRef, useState } from "react";
import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Divider,
} from "@mui/material";
import FileUploadOutlined from "@mui/icons-material/FileUploadOutlined";
import FileDownloadOutlined from "@mui/icons-material/FileDownloadOutlined";
import DataObjectOutlined from "@mui/icons-material/DataObjectOutlined";
import TableChartOutlined from "@mui/icons-material/TableChartOutlined";
import PictureAsPdfOutlined from "@mui/icons-material/PictureAsPdfOutlined";
import { useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import { useChatbotSlots, useChatbotActions } from "../../hooks/useChatbot";
import { fetchData, importChatbotFlowFromPdf, getChatbotFlowImportJob, getAgentById } from "../../api/client";
import { uploadOne } from "../../hooks/useS3Upload";
import { extractPdfTextFromFile } from "../../utils/extractPdfTextClient";
import { getUserFacingApiMessage } from "../../utils/getUserFacingApiMessage";
import {
  slotsToFlowDocument,
  buildFullFlowDocument,
  downloadFlowJson,
  exportFlowExcel,
  exportFlowVisualPdf,
  parseFlowDocument,
  parseExcelFlowFile,
  readFileAsText,
  readFileAsArrayBuffer,
  readFileAsBase64,
} from "../../utils/chatbotFlowExchange";
import ChatbotFlowImportProgress from "./ChatbotFlowImportProgress";
import ChatbotFlowImportHelpDialog from "./ChatbotFlowImportHelpDialog";
import ChatbotFlowImportWizard, { fetchAutomationRuleForExport } from "./ChatbotFlowImportWizard";
import { ensureAlterarSolicitacaoAction } from "../../utils/chatbotAlterarSolicitacaoAction";

/** PDFs maiores que isso vão ao S3 (limite do API Gateway ~10 MB no body). */
const PDF_INLINE_MAX_BYTES = 6 * 1024 * 1024;
/** Enfileiramento deve responder rápido; o processamento roda no worker. */
const PDF_IMPORT_ENQUEUE_TIMEOUT_MS = 60000;
const PDF_IMPORT_POLL_INTERVAL_MS = 2500;
const PDF_IMPORT_POLL_MAX_MS = 1200000;
const SLOTS_REFETCH_INTERVAL_MS = 1500;
const SLOTS_REFETCH_MAX_MS = 120000;

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function fetchChatbotSlots(userId) {
  const params = new URLSearchParams({ user_id: String(userId) });
  const res = await fetchData(`/chatbot/slots?${params.toString()}`, "GET");
  const data = res?.data ?? res ?? [];
  return Array.isArray(data) ? data : [];
}

async function waitForImportedSlots(queryClient, userId) {
  const started = Date.now();
  while (Date.now() - started < SLOTS_REFETCH_MAX_MS) {
    await queryClient.invalidateQueries(["chatbot", "slots", userId]);
    await queryClient.invalidateQueries(["chatbot"]);
    const slots = await fetchChatbotSlots(userId);
    queryClient.setQueryData(["chatbot", "slots", userId, null], slots);
    if (slots.length > 0) return slots;
    await sleep(SLOTS_REFETCH_INTERVAL_MS);
  }
  const slots = await fetchChatbotSlots(userId);
  queryClient.setQueryData(["chatbot", "slots", userId, null], slots);
  return slots;
}

async function pollChatbotFlowImportJob(jobId, { onStatus, t }) {
  const started = Date.now();
  while (Date.now() - started < PDF_IMPORT_POLL_MAX_MS) {
    const job = await getChatbotFlowImportJob(jobId);
    const status = String(job?.status || "").toLowerCase();
    onStatus?.(status, job);

    if (status === "completed") return job;
    if (status === "failed" || status === "cancelled") {
      const err = new Error(job?.errorMessage || t("chatbotConfigPage.flowImportPdfError"));
      err.job = job;
      throw err;
    }

    await sleep(PDF_IMPORT_POLL_INTERVAL_MS);
  }
  const err = new Error(t("chatbotConfigPage.flowImportPdfTimeout"));
  err.code = "poll_timeout";
  throw err;
}

export default function ChatbotFlowImportExport({ userId, chatbotName, disabled, onError, onSuccess }) {
  const { t } = useTranslation("settings");
  const queryClient = useQueryClient();
  const { data: slots = [], isLoading } = useChatbotSlots(userId);
  const { data: actionsCatalog = [] } = useChatbotActions(userId);

  const [exportAnchor, setExportAnchor] = useState(null);
  const [importAnchor, setImportAnchor] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingImport, setPendingImport] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardFlowDoc, setWizardFlowDoc] = useState(null);
  const [wizardSourceLabel, setWizardSourceLabel] = useState("JSON");
  const [importHelpType, setImportHelpType] = useState(null);
  const [busy, setBusy] = useState(false);
  const [importProgress, setImportProgress] = useState(null);

  const alterarSolicitacaoForm = useMemo(() => {
    const catalog = ensureAlterarSolicitacaoAction(actionsCatalog);
    const action = catalog.find((a) => Number(a.tipo_acao) === 4 && Number(a.id_acao) === 4);
    return action?.parameterForm || null;
  }, [actionsCatalog]);

  const patchProgress = (patch) => {
    setImportProgress((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  const jsonInputRef = useRef(null);
  const excelInputRef = useRef(null);
  const pdfInputRef = useRef(null);

  const safeFileName = (chatbotName || "chatbot-fluxo").replace(/[^-\w\s]+/g, "").trim() || "chatbot-fluxo";

  const buildDocument = () => slotsToFlowDocument(slots, { chatbotName, sourceUserId: userId });

  const buildExportLoaders = () => ({
    fetchAutomationRule: fetchAutomationRuleForExport,
    fetchUser: async (id) => {
      const res = await fetchData(`/users/${id}`, "GET");
      return res?.data ?? res;
    },
    fetchAgent: async (id) => getAgentById(id),
    fetchChannel: async (id) => {
      const res = await fetchData(`/channels/${id}`, "GET");
      return res?.data ?? res;
    },
    fetchType: async (id) => {
      const res = await fetchData(`/types/${id}`, "GET");
      return res?.data ?? res;
    },
    fetchSubtype: async (id) => {
      const res = await fetchData(`/types/${id}`, "GET");
      return res?.data ?? res;
    },
    fetchPaymentMethod: async (id) => {
      const res = await fetchData(`/payment-methods/${id}`, "GET");
      return res?.data ?? res;
    },
    fetchDeliveryMethod: async (id) => {
      const res = await fetchData(`/delivery-methods/${id}`, "GET");
      return res?.data ?? res;
    },
    fetchPosition: async (id) => {
      const res = await fetchData(`/positions/${id}`, "GET");
      return res?.data ?? res;
    },
  });

  const handleExportJson = async () => {
    setExportAnchor(null);
    if (!slots.length) {
      onError?.(t("chatbotConfigPage.flowExportEmpty"));
      return;
    }
    setBusy(true);
    try {
      const doc = await buildFullFlowDocument(slots, {
        chatbotName,
        sourceUserId: userId,
        parameterForm: alterarSolicitacaoForm,
        loaders: buildExportLoaders(),
      });
      downloadFlowJson(doc, safeFileName);
      onSuccess?.(t("chatbotConfigPage.flowExportSuccess"));
    } catch {
      downloadFlowJson(buildDocument(), safeFileName);
      onSuccess?.(t("chatbotConfigPage.flowExportSuccess"));
    } finally {
      setBusy(false);
    }
  };

  const handleExportExcel = () => {
    setExportAnchor(null);
    if (!slots.length) {
      onError?.(t("chatbotConfigPage.flowExportEmpty"));
      return;
    }
    exportFlowExcel(buildDocument(), safeFileName);
    onSuccess?.(t("chatbotConfigPage.flowExportSuccess"));
  };

  const handleExportPdf = () => {
    setExportAnchor(null);
    if (!slots.length) {
      onError?.(t("chatbotConfigPage.flowExportEmpty"));
      return;
    }
    exportFlowVisualPdf(buildDocument(), safeFileName);
    onSuccess?.(t("chatbotConfigPage.flowExportSuccess"));
  };

  const runImport = async (flowDoc, sourceLabel) => {
    setBusy(true);
    try {
      await fetchData("/chatbot/slots/import", "POST", {
        user_id: userId,
        flow: flowDoc,
        replace: true,
      });
      await queryClient.invalidateQueries(["chatbot", "slots", userId]);
      await queryClient.invalidateQueries(["chatbot"]);
      onSuccess?.(t("chatbotConfigPage.flowImportSuccess", { source: sourceLabel }));
      return true;
    } catch (err) {
      onError?.(err?.message || t("chatbotConfigPage.flowImportError"));
      throw err;
    } finally {
      setBusy(false);
      setConfirmOpen(false);
      setPendingImport(null);
    }
  };

  const runPdfImport = async (file) => {
    setConfirmOpen(false);
    setBusy(true);
    setImportProgress({
      open: true,
      phase: "extracting",
      fileName: file.name,
      logs: [],
      page: 0,
      totalPages: 0,
    });

    try {
      const payload = {
        user_id: userId,
        filename: file.name,
        replace: true,
      };

      let pdfText = null;
      try {
        pdfText = await extractPdfTextFromFile(file, {
          onProgress: (page, total) => {
            patchProgress({ phase: "extracting", page, totalPages: total });
          },
        });
      } catch {
        pdfText = null;
      }

      if (pdfText) {
        payload.pdf_text = pdfText;
      } else if (file.size > PDF_INLINE_MAX_BYTES) {
        patchProgress({ phase: "uploading" });
        const { key } = await uploadOne(file, { prefix: "chatbot-flow-imports" });
        payload.s3_key = key;
      } else {
        payload.file_base64 = await readFileAsBase64(file);
      }

      patchProgress({ phase: "sending", logs: [] });

      const enqueue = await importChatbotFlowFromPdf(payload, {
        timeout: PDF_IMPORT_ENQUEUE_TIMEOUT_MS,
      });

      if (enqueue?.accepted && enqueue?.jobId) {
        patchProgress({
          phase: "queued",
          jobId: enqueue.jobId,
          serverStatus: "queued",
          logs: [t("chatbotConfigPage.flowImportPdfQueued")],
        });
        await pollChatbotFlowImportJob(enqueue.jobId, {
          t,
          onStatus: (status, job) => {
            const logs = Array.isArray(job?.log) ? job.log : [];
            patchProgress({
              phase: status === "queued" ? "queued" : "running",
              serverStatus: status,
              logs: logs.length ? logs : undefined,
            });
          },
        });
      } else {
        patchProgress({ phase: "sync" });
      }

      patchProgress({ phase: "saving", serverStatus: undefined });
      await waitForImportedSlots(queryClient, userId);
      patchProgress({ phase: "completed" });
      onSuccess?.(t("chatbotConfigPage.flowImportPdfSuccess"));
    } catch (err) {
      const status = err?.response?.status;
      const isTimeout =
        status === 504 ||
        status === 503 ||
        err?.code === "ECONNABORTED" ||
        err?.code === "poll_timeout" ||
        /timeout|timed?\s*out|service unavailable/i.test(String(err?.message || ""));
      const { message } = getUserFacingApiMessage(err);
      const errMsg = isTimeout
        ? t("chatbotConfigPage.flowImportPdfTimeout")
        : message || err?.message || t("chatbotConfigPage.flowImportPdfError");
      patchProgress({
        phase: "failed",
        error: errMsg,
        logs: Array.isArray(err?.job?.log) ? err.job.log : undefined,
      });
      onError?.(errMsg);
    } finally {
      setBusy(false);
      setPendingImport(null);
    }
  };

  const requestImportConfirm = (payload) => {
    setPendingImport(payload);
    setConfirmOpen(true);
  };

  const openImportWizard = (flowDoc, label) => {
    setWizardFlowDoc(flowDoc);
    setWizardSourceLabel(label);
    setWizardOpen(true);
  };

  const handleJsonFile = async (file) => {
    if (!file) return;
    try {
      const text = await readFileAsText(file);
      const flowDoc = parseFlowDocument(text);
      openImportWizard(flowDoc, "JSON");
    } catch {
      onError?.(t("chatbotConfigPage.flowImportInvalidJson"));
    }
  };

  const handleExcelFile = async (file) => {
    if (!file) return;
    try {
      const buffer = await readFileAsArrayBuffer(file);
      const flowDoc = parseExcelFlowFile(buffer, chatbotName);
      parseFlowDocument(flowDoc);
      openImportWizard(flowDoc, "Excel");
    } catch {
      onError?.(t("chatbotConfigPage.flowImportInvalidExcel"));
    }
  };

  const handlePdfFile = async (file) => {
    if (!file) return;
    requestImportConfirm({ type: "pdf", file, label: "PDF" });
  };

  const openFilePickerForImportType = (type) => {
    if (type === "json") jsonInputRef.current?.click();
    else if (type === "excel") excelInputRef.current?.click();
    else if (type === "pdf") pdfInputRef.current?.click();
  };

  const handleConfirmImport = () => {
    if (!pendingImport) return;
    if (pendingImport.type === "pdf") {
      runPdfImport(pendingImport.file);
      return;
    }
    runImport(pendingImport.flowDoc, pendingImport.label).catch(() => {});
  };

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        startIcon={<FileDownloadOutlined />}
        onClick={(e) => setExportAnchor(e.currentTarget)}
        disabled={disabled || isLoading || busy}
        sx={{ textTransform: "none" }}
      >
        {t("chatbotConfigPage.flowExport")}
      </Button>
      <Menu anchorEl={exportAnchor} open={!!exportAnchor} onClose={() => setExportAnchor(null)}>
        <MenuItem onClick={handleExportJson}>
          <ListItemIcon>
            <DataObjectOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t("chatbotConfigPage.flowExportJson")} secondary={t("chatbotConfigPage.flowExportJsonHint")} />
        </MenuItem>
        <MenuItem onClick={handleExportExcel}>
          <ListItemIcon>
            <TableChartOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t("chatbotConfigPage.flowExportExcel")} secondary={t("chatbotConfigPage.flowExportExcelHint")} />
        </MenuItem>
        <MenuItem onClick={handleExportPdf}>
          <ListItemIcon>
            <PictureAsPdfOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t("chatbotConfigPage.flowExportPdf")} secondary={t("chatbotConfigPage.flowExportPdfHint")} />
        </MenuItem>
      </Menu>

      <Button
        size="small"
        variant="outlined"
        startIcon={busy ? <CircularProgress size={16} /> : <FileUploadOutlined />}
        onClick={(e) => setImportAnchor(e.currentTarget)}
        disabled={disabled || busy}
        sx={{ textTransform: "none" }}
      >
        {t("chatbotConfigPage.flowImport")}
      </Button>
      <Menu anchorEl={importAnchor} open={!!importAnchor} onClose={() => setImportAnchor(null)}>
        <MenuItem
          onClick={() => {
            setImportAnchor(null);
            setImportHelpType("json");
          }}
        >
          <ListItemIcon>
            <DataObjectOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t("chatbotConfigPage.flowImportJson")} />
        </MenuItem>
        <MenuItem
          onClick={() => {
            setImportAnchor(null);
            setImportHelpType("excel");
          }}
        >
          <ListItemIcon>
            <TableChartOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t("chatbotConfigPage.flowImportExcel")} />
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            setImportAnchor(null);
            setImportHelpType("pdf");
          }}
        >
          <ListItemIcon>
            <PictureAsPdfOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={t("chatbotConfigPage.flowImportPdf")}
            secondary={t("chatbotConfigPage.flowImportPdfHint")}
          />
        </MenuItem>
      </Menu>

      <input
        ref={jsonInputRef}
        type="file"
        accept=".json,application/json"
        hidden
        onChange={(e) => {
          handleJsonFile(e.target.files?.[0]);
          e.target.value = "";
        }}
      />
      <input
        ref={excelInputRef}
        type="file"
        accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        hidden
        onChange={(e) => {
          handleExcelFile(e.target.files?.[0]);
          e.target.value = "";
        }}
      />
      <input
        ref={pdfInputRef}
        type="file"
        accept=".pdf,application/pdf"
        hidden
        onChange={(e) => {
          handlePdfFile(e.target.files?.[0]);
          e.target.value = "";
        }}
      />

      <ChatbotFlowImportProgress
        progress={importProgress}
        onClose={() => setImportProgress(null)}
      />

      <ChatbotFlowImportHelpDialog
        type={importHelpType}
        open={Boolean(importHelpType)}
        onClose={() => setImportHelpType(null)}
        onSelectFile={openFilePickerForImportType}
      />

      <ChatbotFlowImportWizard
        open={wizardOpen}
        flowDoc={wizardFlowDoc}
        parameterForm={alterarSolicitacaoForm}
        sourceLabel={wizardSourceLabel}
        onClose={() => {
          if (busy) return;
          setWizardOpen(false);
          setWizardFlowDoc(null);
        }}
        onConfirm={async (mappedFlow) => {
          await runImport(mappedFlow, wizardSourceLabel);
          setWizardOpen(false);
          setWizardFlowDoc(null);
        }}
      />

      <Dialog open={confirmOpen} onClose={busy ? undefined : () => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t("chatbotConfigPage.flowImportConfirmTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("chatbotConfigPage.flowImportConfirmBody", {
              source: pendingImport?.label || "",
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={busy}>
            {t("shared.cancel")}
          </Button>
          <Button onClick={handleConfirmImport} variant="contained" disabled={busy} autoFocus>
            {busy ? <CircularProgress size={20} /> : t("chatbotConfigPage.flowImportConfirmAction")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
